import { useState, type FormEvent, type ReactNode } from 'react';
import { weather, type NajdenaObec } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { Button, Card, DataRow, Field, Notice, PageHeader } from '../components/ui.js';
import { IconCloudSun, IconRefresh } from '../components/icons.js';
import { useSession } from '../auth/session.js';

/*
 * Stranka Pocasie.
 *
 * Vlastna stranka, nie karta v Integraciach: nastavenie je podobne
 * rozsiahle ako pri Tuya alebo Zigbee - poloha, horizonty, rucne
 * stiahnutie - a v zozname integracii by to bola najdlhsia dlazdica
 * zo vsetkych (ziadost zadavatela 2026-09-16).
 */

/*
 * Pocasie.
 *
 * Nastavenie je jedno: KDE. Suradnice sa nezadavaju rucne - clovek
 * napise meno obce a vyberie zo zoznamu, lebo rozdiel medzi 49,22 a
 * 49,32 na mape nikto neodhadne.
 *
 * Karta hovori aj to, co odchadza von zo siete. Je to prva integracia,
 * ktora vobec nieco von posiela, a zamlcat to by bolo horsie nez samotne
 * odoslanie.
 */
const HODINOVE_HORIZONTY = [1, 3, 6, 12, 24] as const;
const DENNE_HORIZONTY = [0, 1, 2, 3] as const;

const NAZOV_DNA: Record<number, string> = {
  0: 'Dnes',
  1: 'Zajtra',
  2: 'O 2 dni',
  3: 'O 3 dni',
};

export function Weather(): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const nacitanie = useApi((signal) => weather.prehlad(signal));

  const [dotaz, setDotaz] = useState('');
  const [najdene, setNajdene] = useState<NajdenaObec[] | undefined>(undefined);
  const [hlada, setHlada] = useState(false);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [hotovo, setHotovo] = useState<string | undefined>(undefined);
  const [hodiny, setHodiny] = useState<number[]>([3, 6, 12]);
  const [dni, setDni] = useState<number[]>([0, 1]);

  const data = nacitanie.data;
  const nastavenia = data?.settings ?? null;

  const prepni = (zoznam: number[], hodnota: number, nastav: (n: number[]) => void): void => {
    nastav(
      zoznam.includes(hodnota)
        ? zoznam.filter((h) => h !== hodnota)
        : [...zoznam, hodnota].sort((a, b) => a - b),
    );
  };

  const hladaj = (event: FormEvent): void => {
    event.preventDefault();
    setChyba(undefined);
    setHotovo(undefined);
    setHlada(true);
    void weather
      .hladaj(dotaz)
      .then(({ places }) => setNajdene(places))
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Obec sa nepodarilo nájsť.');
      })
      .finally(() => setHlada(false));
  };

  const uloz = (obec: NajdenaObec): void => {
    setChyba(undefined);
    void weather
      .uloz({
        latitude: obec.latitude,
        longitude: obec.longitude,
        placeName: obec.name,
        hourlyHorizons: hodiny,
        dailyDays: dni,
      })
      .then(() => {
        setHotovo(`Poloha nastavená na ${obec.name}.`);
        setNajdene(undefined);
        setDotaz('');
        nacitanie.refresh();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Polohu sa nepodarilo uložiť.');
      });
  };

  const zmenHorizonty = (): void => {
    if (!nastavenia) return;
    setChyba(undefined);
    void weather
      .uloz({
        latitude: nastavenia.latitude,
        longitude: nastavenia.longitude,
        placeName: nastavenia.placeName,
        hourlyHorizons: hodiny,
        dailyDays: dni,
      })
      .then(() => {
        setHotovo('Zoznam predpovedí upravený.');
        nacitanie.refresh();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zmenu sa nepodarilo uložiť.');
      });
  };

  const zabudni = (): void => {
    setChyba(undefined);
    void weather
      .zabudni()
      .then(() => {
        setHotovo('Poloha zabudnutá, zariadenia počasia zmizli zo zoznamu.');
        nacitanie.refresh();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Polohu sa nepodarilo zabudnúť.');
      });
  };

  const stiahni = (): void => {
    setChyba(undefined);
    void weather
      .obnov()
      .then(({ refresh }) => {
        setHotovo(`Stiahnuté, ${refresh.devices} zariadení má nové hodnoty.`);
        nacitanie.refresh();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Počasie sa nepodarilo stiahnuť.');
      });
  };

  /*
   * Nacitavanie a chyba maju vlastny stav, nie tiche nic. Ked sa karta
   * neukaze, clovek nevie, ci integracia neexistuje alebo sa len
   * nenacitala.
   */
  if (data === undefined) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Počasie" detail="Predpoveď z Open-Meteo pre Miniserver." />
        <Card className="p-4 sm:p-5">
          {nacitanie.error === undefined ? (
            <p className="text-sm text-(--color-text-subtle)">Načítavam…</p>
          ) : (
            <Notice tone="danger">{nacitanie.error}</Notice>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Počasie"
        detail="Predpoveď z Open-Meteo ako bežné zariadenia pre Miniserver."
        action={
          <Button
            onClick={nacitanie.reload}
            icon={<IconRefresh className="size-4" />}
            variant="ghost"
          >
            Obnoviť
          </Button>
        }
      />
      <Card className="flex flex-col gap-4 p-4 sm:p-5">
        <div className="flex items-start gap-2.5">
          <span className="text-(--color-text-subtle)">
            <IconCloudSun />
          </span>
          <p className="text-sm text-(--color-text-muted)">
            Predpoveď z Open-Meteo ako bežné zariadenia — teplota, vietor, nárazy, oblačnosť,
            slnečné žiarenie, zrážky. Do Miniservera ide tou istou cestou ako Tuya a Zigbee.
          </p>
        </div>

        {nastavenia ? (
          <>
            <DataRow label="Obec">{nastavenia.placeName}</DataRow>
            <DataRow label="Naposledy stiahnuté">
              {nastavenia.lastFetchAt === null
                ? 'Zatiaľ nikdy'
                : new Date(nastavenia.lastFetchAt).toLocaleString('sk-SK')}
            </DataRow>
            <DataRow label="Stav">{data.health.detail}</DataRow>
            <DataRow label="Zariadení">{data.health.deviceCount}</DataRow>
          </>
        ) : (
          <Notice tone="warn">
            Poloha nie je nastavená, takže sa nesťahuje nič. Napíš názov obce a vyber zo zoznamu.
          </Notice>
        )}

        {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
        {hotovo !== undefined && <Notice tone="ok">{hotovo}</Notice>}

        {jeSpravca ? (
          <>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-(--color-text)">Hodinová predpoveď</span>
              <div className="flex flex-wrap gap-2">
                {HODINOVE_HORIZONTY.map((h) => (
                  <Button
                    key={h}
                    variant={hodiny.includes(h) ? 'primary' : 'ghost'}
                    onClick={() => prepni(hodiny, h, setHodiny)}
                  >
                    o {h} h
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-(--color-text)">Denná predpoveď</span>
              <div className="flex flex-wrap gap-2">
                {DENNE_HORIZONTY.map((d) => (
                  <Button
                    key={d}
                    variant={dni.includes(d) ? 'primary' : 'ghost'}
                    onClick={() => prepni(dni, d, setDni)}
                  >
                    {NAZOV_DNA[d]}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-(--color-text-subtle)">
                Každý deň dá dve zariadenia: denný súhrn s maximom a druhé s nočným minimom. Jedna
                schopnosť nesie jednu hodnotu, takže sa do jedného nezmestia.
              </p>
            </div>

            <form className="flex flex-wrap items-end gap-2" onSubmit={hladaj}>
              <div className="min-w-56 flex-1">
                <Field
                  label="Obec"
                  value={dotaz}
                  onChange={setDotaz}
                  required
                  placeholder="Žilina"
                  hint="Aspoň dva znaky. Rovnomenné obce rozlíši okres."
                />
              </div>
              <Button type="submit" variant="primary" disabled={hlada}>
                {hlada ? 'Hľadám…' : 'Nájsť'}
              </Button>
            </form>

            {najdene !== undefined &&
              (najdene.length === 0 ? (
                <Notice tone="warn">Taká obec sa nenašla. Skús iný zápis názvu.</Notice>
              ) : (
                <ul className="flex flex-col gap-2">
                  {najdene.map((obec) => (
                    <li
                      key={`${obec.latitude},${obec.longitude}`}
                      className="flex flex-wrap items-center justify-between gap-2 text-sm"
                    >
                      <span className="text-(--color-text)">
                        {obec.name}
                        <span className="text-(--color-text-subtle)">
                          {obec.admin === null ? '' : `, ${obec.admin}`}
                          {obec.country === '' ? '' : `, ${obec.country}`}
                        </span>
                      </span>
                      <Button onClick={() => uloz(obec)}>Použiť</Button>
                    </li>
                  ))}
                </ul>
              ))}

            {nastavenia && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={zmenHorizonty}>Uložiť výber predpovedí</Button>
                <Button onClick={stiahni} icon={<IconRefresh className="size-4" />}>
                  Stiahnuť teraz
                </Button>
                <Button onClick={zabudni} variant="ghost">
                  Zabudnúť polohu
                </Button>
              </div>
            )}
          </>
        ) : (
          <Notice tone="warn">Polohu môže nastaviť len správca.</Notice>
        )}

        <p className="text-xs text-(--color-text-subtle)">
          Von zo siete odchádza len poloha, zaokrúhlená na približne kilometer — mriežka modelu má
          aj tak 2 až 11 km. Žiadny účet, žiadny kľúč, žiadne údaje o zariadeniach.
        </p>
      </Card>
    </div>
  );
}
